<template>
  <div :class="containerClass">
    <page-header :title="title" :container-class="null" :show-back="true" class="mb-32pt" />

    <div class="page-section pt-0">
      <b-card>
        <b-overlay :show="isLoading" rounded="sm" opacity="0.4">
          <template #overlay>
            <div class="text-center">
              <b-spinner class="mb-2" />
              <div>Loading attempts...</div>
            </div>
          </template>

          <div class="assignment-header-section mb-4">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div class="h5 mb-1 font-weight-bold text-dark">{{ assignmentTitle || 'Assignment' }}</div>
                <div class="text-muted small">
                  <i class="fas fa-user-graduate mr-1"></i>
                  Student ID: {{ studentId }}
                </div>
              </div>
              <div class="total-attempts-badge" v-if="attempts.length">
                <div class="attempts-number">{{ attempts.length }}</div>
                <div class="attempts-label">Total Attempts</div>
              </div>
            </div>

            <!-- Attempts Summary Stats -->
            <div class="stats-cards" v-if="assignmentInfo && get(assignmentInfo, 'is_graded')">
              <div class="stat-card stat-best" v-if="bestPercentage !== null">
                <div class="stat-icon">
                  <i class="fas fa-trophy"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-label">Best Performance</div>
                  <div class="stat-value">{{ bestPercentage }}%</div>
                </div>
              </div>

              <div class="stat-card stat-highest" v-if="bestScore !== null && get(assignmentInfo, 'total_marks')">
                <div class="stat-icon">
                  <i class="fas fa-star"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-label">Highest Marks</div>
                  <div class="stat-value">{{ bestScore }}/{{ get(assignmentInfo, 'total_marks') }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!attempts.length" class="empty-state">
            No attempts found for this assignment.
          </div>

          <div v-else>
            <b-card no-body v-for="(attempt, index) in attempts" :key="attempt.id" class="mb-3 attempt-card">
              <b-card-header header-tag="header" class="p-0" role="tab">
                <b-button
                  block
                  v-b-toggle="`attempt-${attempt.id}`"
                  variant="link"
                  class="text-left text-decoration-none attempt-header"
                >
                  <div class="d-flex justify-content-between align-items-center w-100">
                    <div class="d-flex align-items-center">
                      <div class="chevron-icon mr-3">
                        <i class="fas fa-chevron-right"></i>
                      </div>
                      <div class="attempt-title">Attempt #{{ attempts.length - index }}</div>
                    </div>
                    <div class="d-flex align-items-center">
                      <span 
                        v-if="get(assignmentInfo, 'is_graded') && attempt.grade" 
                        class="status-badge mr-4" 
                        :class="getReviewStatusClass(attempt.grade.review_status)"
                      >
                        {{ formatStatus(attempt.grade.review_status) }}
                      </span>
                      <span class="progress-badge mr-4">{{ attempt.progress }}% Complete</span>
                      <span class="date-text">{{ formatDateTime(attempt.start_time) }}</span>
                    </div>
                  </div>
                </b-button>
              </b-card-header>
              <b-collapse :id="`attempt-${attempt.id}`" accordion="attempts-accordion" role="tabpanel">
                <b-card-body class="attempt-content">
                  <!-- Attempt Summary -->
                  <div class="attempt-summary mb-4">
                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                      <div class="d-flex align-items-center mb-2">
                        <div>
                          <div class="font-weight-bold text-dark mb-1">Attempt #{{ attempts.length - index }}</div>
                          <div class="text-muted small">
                            <i class="far fa-calendar-alt mr-1"></i>
                            Started: {{ formatDateTime(attempt.start_time) }}
                          </div>
                          <div class="text-muted small" v-if="attempt.end_time">
                            <i class="far fa-calendar-check mr-1"></i>
                            Completed: {{ formatDateTime(attempt.end_time) }}
                          </div>
                        </div>
                      </div>
                      <div class="attempt-meta">
                        <b-badge variant="light" class="px-3 py-2 mr-2">
                          <i class="fas fa-check-circle mr-1"></i>
                          {{ attempt.completed_tasks }}/{{ attempt.total_tasks }} Tasks
                        </b-badge>
                        <b-badge 
                          v-if="attempt.grade" 
                          :variant="getGradeVariant(attempt.grade)"
                          class="px-3 py-2"
                        >
                          <i class="fas fa-award mr-1"></i>
                          {{ formatGrade(attempt.grade) }}
                        </b-badge>
                      </div>
                    </div>
                  </div>

                  <!-- Tasks -->
                  <div class="tasks-container">
                    <div v-for="(task, taskKey, idx) in get(attempt, 'extras.tasks', {})" :key="taskKey" class="task-card mb-3">
                      <div class="task-header">
                        <div class="d-flex align-items-start justify-content-between">
                          <div class="d-flex align-items-start flex-grow-1">
                            <div class="task-number-badge" :style="{ backgroundColor: primaryColor }">{{ idx + 1 }}</div>
                            <div class="task-text flex-grow-1">{{ task.title }}</div>
                          </div>
                          <div class="task-type-badge">
                            <span class="type-label">
                              {{ getTaskTypeLabel(task.type) }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="task-body">
                        <!-- Upload type -->
                        <div v-if="task.type === 'upload'" class="upload-section">
                          <div v-if="get(task, 'evidence.file')" class="file-attachment">
                            <div class="file-label">
                              <i class="fas fa-paperclip mr-2"></i>Submitted File
                            </div>
                            <a
                              :href="get(task, 'evidence.file')"
                              target="_blank"
                              rel="noopener"
                              download
                              class="download-btn"
                              :style="{ backgroundColor: primaryColor }"
                            >
                              <i class="fas fa-download mr-2"></i>Download File
                            </a>
                          </div>
                          <div v-if="get(task, 'evidence.note')" class="submission-note">
                            <div class="note-label">
                              <i class="fas fa-comment-alt mr-2"></i>Additional Note
                            </div>
                            <div class="note-content" v-safe-html="get(task, 'evidence.note', '')"></div>
                          </div>
                          <div v-if="!get(task, 'evidence.file') && !get(task, 'evidence.note')" class="no-submission">
                            <i class="fas fa-info-circle mr-2"></i>No file uploaded
                          </div>
                        </div>

                        <!-- Text type -->
                        <div v-else-if="task.type === 'text'" class="text-section">
                          <div v-if="get(task, 'evidence.note')" class="text-submission">
                            <div class="submission-label">
                              <i class="fas fa-file-alt mr-2"></i>Student's Response
                            </div>
                            <div class="text-content" v-safe-html="get(task, 'evidence.note', '')"></div>
                          </div>
                          <div v-else class="no-submission">
                            <i class="fas fa-info-circle mr-2"></i>No text submission
                          </div>
                        </div>

                        <!-- Case Study type -->
                        <div v-else-if="task.type === 'case_study'" class="text-section">
                          <div v-if="get(task, 'evidence.note')" class="text-submission">
                            <div class="submission-label">
                              <i class="fas fa-book-reader mr-2"></i>Case Study Response
                            </div>
                            <div class="text-content" v-safe-html="get(task, 'evidence.note', '')"></div>
                          </div>
                          <div v-else class="no-submission">
                            <i class="fas fa-info-circle mr-2"></i>No case study submission
                          </div>
                        </div>

                        <!-- Scenario interview: what the student actually said -->
                        <div v-else-if="task.type === 'scenario'" class="interview-section">
                          <div class="interview-label">
                            <i class="fas fa-microphone-alt mr-2"></i>Interview Transcript
                          </div>
                          <div class="interview-meta mb-2">
                            <span v-if="interviewFor(attempt, taskKey).simulation_title">
                              {{ interviewFor(attempt, taskKey).simulation_title }}
                            </span>
                            <span v-if="interviewFor(attempt, taskKey).call_duration">
                              &middot; {{ formatDuration(interviewFor(attempt, taskKey).call_duration) }}
                            </span>
                          </div>
                          <div v-if="interviewFor(attempt, taskKey).call_summary" class="interview-summary mb-2">
                            <strong>Summary:</strong> {{ interviewFor(attempt, taskKey).call_summary }}
                          </div>
                          <pre
                            v-if="interviewFor(attempt, taskKey).transcript"
                            class="transcript-content"
                          >{{ interviewFor(attempt, taskKey).transcript }}</pre>
                          <div v-else class="text-muted font-italic">
                            No transcript was recorded for this interview.
                          </div>
                        </div>

                        <!-- Checkbox type -->
                        <div v-else class="checkbox-section">
                          <div v-if="String(task.status).toLowerCase() === 'done'" class="checkbox-completed">
                            <i class="fas fa-check-square mr-2"></i>Task marked as completed
                          </div>
                          <div v-else class="checkbox-pending">
                            <i class="far fa-square mr-2"></i>Task not yet completed
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Grading Information -->
                  <div v-if="get(assignmentInfo, 'is_graded')" class="grading-section mt-4">
                    <div class="grading-card">
                      <div class="grading-header">
                        <i class="fas fa-clipboard-check mr-2"></i>Grading Information
                      </div>
                      <div class="grading-body">
                        <div v-if="attempt.grade">
                          <div class="grading-row">
                            <span class="grading-label">Review Status:</span>
                            <b-badge :variant="getReviewStatusVariant(attempt.grade.review_status)">
                              {{ formatStatus(attempt.grade.review_status) }}
                            </b-badge>
                          </div>
                          <div class="grading-row" v-if="attempt.grade.score_obtained !== null">
                            <span class="grading-label">Score:</span>
                            <span class="grading-value">{{ attempt.grade.score_obtained }}/{{ attempt.grade.total_marks }} ({{ attempt.grade.percentage }}%)</span>
                          </div>
                          <div class="grading-row" v-if="attempt.grade.graded_at">
                            <span class="grading-label">Graded At:</span>
                            <span class="grading-value">{{ formatDateTime(attempt.grade.graded_at) }}</span>
                          </div>
                          <div class="grading-feedback" v-if="attempt.grade.overall_feedback">
                            <div class="feedback-label">
                              <i class="fas fa-comment-dots mr-2"></i>Feedback
                            </div>
                            <div class="feedback-content">{{ attempt.grade.overall_feedback }}</div>
                          </div>
                        </div>
                        
                        <!-- Grade Assignment Button for Pending Submissions -->
                        <div v-if="!attempt.grade || String(attempt.grade.review_status).toLowerCase() === 'pending'" class="text-center py-3">
                          <b-button 
                            variant="primary" 
                            :style="{ backgroundColor: primaryColor, borderColor: primaryColor }"
                            @click="gradeAssignment(attempt)"
                          >
                            <i class="fas fa-edit mr-2"></i>Grade Assignment
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-card-body>
              </b-collapse>
            </b-card>
          </div>
        </b-overlay>
      </b-card>
    </div>
  </div>
</template>

<script>
import { formatDateTime } from '@/common/utils';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import axios from 'axios';
import { get, startCase } from 'lodash';
import { mapGetters } from 'vuex';

export default {
  name: 'AssignmentAttempts',
  components: { PageHeader },
  extends: Page,
  data() {
    return {
      title: 'Assignment Attempts',
      isLoading: false,
      attempts: [],
      assignmentTitle: '',
      assignmentInfo: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    primaryColor() {
      return get(this.getLoggedInUser, 'linked_entity.primary_color', '#1a1a1a');
    },
    enrollmentId() {
      return this.$route.params.enrollmentId;
    },
    assignmentId() {
      return this.$route.params.assignmentId;
    },
    studentId() {
      return this.$route.query.student_id;
    },
    lessonId() {
      return this.$route.query.lesson_id;
    },
    bestPercentage() {
      if (!this.attempts.length) return null;
      const firstAttempt = this.attempts[0];
      return get(firstAttempt, 'best_percentage', null);
    },
    bestScore() {
      if (!this.attempts.length) return null;
      const firstAttempt = this.attempts[0];
      return get(firstAttempt, 'best_score', null);
    },
  },
  methods: {
    get,
    formatDateTime,

    interviewFor(attempt, taskId) {
      return get(attempt, `scenario_interviews.${taskId}`, {}) || {};
    },

    formatDuration(seconds) {
      const total = Math.round(Number(seconds) || 0);
      const mins = Math.floor(total / 60);
      const secs = total % 60;
      return `${mins}m ${String(secs).padStart(2, '0')}s`;
    },

    formatStatus(value) {
      if (!value) return '-';
      const map = {
        completed: 'Completed',
        in_progress: 'In Progress',
        pending: 'Pending',
        graded: 'Graded',
        reviewed: 'Reviewed',
      };
      const normalized = String(value).toLowerCase();
      return map[normalized] || startCase(normalized.replace(/[-_]/g, ' '));
    },
    getStatusClass(status) {
      const normalized = String(status).toLowerCase();
      if (normalized === 'completed') return 'status-completed';
      if (normalized === 'in_progress') return 'status-in-progress';
      return 'status-pending';
    },
    getTaskTypeLabel(type) {
      const map = {
        upload: 'File Upload',
        text: 'Text Response',
        case_study: 'Case Study',
        checkbox: 'Checkbox',
      };
      return map[type] || startCase(type);
    },
    getGradeVariant(grade) {
      if (!grade) return 'secondary';
      const status = String(grade.review_status).toLowerCase();
      if (status === 'graded' || status === 'reviewed') return 'success';
      if (status === 'pending') return 'warning';
      return 'secondary';
    },
    getReviewStatusVariant(status) {
      const normalized = String(status).toLowerCase();
      if (normalized === 'graded' || normalized === 'reviewed') return 'success';
      if (normalized === 'pending') return 'warning';
      return 'secondary';
    },
    getReviewStatusClass(status) {
      const normalized = String(status).toLowerCase();
      if (normalized === 'graded' || normalized === 'reviewed') return 'status-completed';
      if (normalized === 'pending') return 'status-pending';
      return 'status-pending';
    },
    formatGrade(grade) {
      if (!grade) return 'Not Graded';
      if (grade.score_obtained !== null && grade.total_marks !== null) {
        return `${grade.score_obtained}/${grade.total_marks}`;
      }
      return this.formatStatus(grade.review_status);
    },
    gradeAssignment(attempt) {
      if (!attempt || !attempt.id) return;
      const tab = this.$route.query.tab;
      // Navigate to grade assignment submission page
      this.$router.push({
        name: 'grade-submission',
        params: { id: attempt.id },
        query: {
          student_id: this.studentId ? String(this.studentId) : undefined,
          lesson_id: this.lessonId ? String(this.lessonId) : undefined,
          ...(tab !== undefined && { tab }),
        },
      });
    },
    async fetchAttempts() {
      if (!this.assignmentId || !this.studentId) return;
      this.isLoading = true;
      try {
        let url = `api/lms/assignment-submissions/attempts/?assignment_id=${encodeURIComponent(this.assignmentId)}&student_id=${encodeURIComponent(this.studentId)}`;
        if (this.lessonId) {
          url += `&lesson_id=${encodeURIComponent(this.lessonId)}`;
        } else {
          // If lessonId is not in query, try to get it from route or use empty string
          const routeLessonId = this.$route.query.lesson_id;
          if (routeLessonId) {
            url += `&lesson_id=${encodeURIComponent(routeLessonId)}`;
          }
        }
        const res = await axios.get(url);
        const data = get(res, 'data') || [];
        this.attempts = Array.isArray(data) ? data : [];
        
        if (this.attempts.length > 0) {
          const firstAttempt = this.attempts[0];
          this.assignmentInfo = get(firstAttempt, 'assignment');
          this.assignmentTitle = get(this.assignmentInfo, 'title') || 'Assignment Attempts';
          this.title = `${this.assignmentTitle} • Attempts`;
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.get(e, 'response.data.message') || 'Failed to load attempts.' });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    await this.fetchAttempts();
  },
  watch: {
    '$route.fullPath'() {
      this.fetchAttempts();
    },
  },
};
</script>

<style scoped>
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

/* Assignment Header Section */
.assignment-header-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.total-attempts-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  min-width: 100px;
}

.total-attempts-badge .attempts-number {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.total-attempts-badge .attempts-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  font-weight: 600;
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 10px;
  padding: 1.25rem;
  border: 2px solid;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-card .stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  font-size: 1.25rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.stat-card .stat-content {
  flex-grow: 1;
}

.stat-card .stat-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 0.25rem;
  opacity: 0.8;
}

.stat-card .stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

/* Average Score Card */
.stat-average {
  border-color: #93c5fd;
  background: linear-gradient(135deg, #dbeafe 0%, #ffffff 100%);
}

.stat-average .stat-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;
}

.stat-average .stat-label {
  color: #1e40af;
}

.stat-average .stat-value {
  color: #1d4ed8;
}

/* Best Performance Card */
.stat-best {
  border-color: #fcd34d;
  background: linear-gradient(135deg, #fef3c7 0%, #ffffff 100%);
}

.stat-best .stat-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #ffffff;
}

.stat-best .stat-label {
  color: #92400e;
}

.stat-best .stat-value {
  color: #b45309;
}

/* Highest Score Card */
.stat-highest {
  border-color: #86efac;
  background: linear-gradient(135deg, #d1fae5 0%, #ffffff 100%);
}

.stat-highest .stat-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
}

.stat-highest .stat-label {
  color: #065f46;
}

.stat-highest .stat-value {
  color: #047857;
}

/* Collapsible Card Styling */
.attempt-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.attempt-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-color: #d1d5db;
}

.attempt-header {
  padding: 1rem 1.25rem;
  color: #374151;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.attempt-header:hover {
  background-color: #f9fafb;
}

.attempt-header:focus {
  box-shadow: none;
  outline: none;
}

/* Chevron Icon Animation */
.chevron-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
  color: #6b7280;
}

.attempt-header:not(.collapsed) .chevron-icon {
  transform: rotate(90deg);
}

/* Attempt Title and Meta */
.attempt-title {
  font-weight: 600;
  font-size: 1rem;
  color: #111827;
  white-space: nowrap;
}

.status-badge {
  font-weight: 600;
  font-size: 0.8125rem;
  white-space: nowrap;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
}

.status-completed {
  color: #065f46;
  background: #d1fae5;
}

.status-in-progress {
  color: #1e40af;
  background: #dbeafe;
}

.status-pending {
  color: #92400e;
  background: #fef3c7;
}

.progress-badge {
  font-weight: 600;
  font-size: 0.8125rem;
  white-space: nowrap;
  color: #374151;
  background: #f3f4f6;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
}

.date-text {
  font-size: 0.875rem;
  color: #9ca3af;
  white-space: nowrap;
}

/* Content Area */
.attempt-content {
  padding: 1.5rem;
  background: #fafafa;
  border-top: 1px solid #e5e7eb;
}

/* Attempt Summary */
.attempt-summary {
  background: #ffffff;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
}

.performance-circle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid var(--secondary-color, #6b7280);
  background: #ffffff;
  transition: all 0.3s ease;
}

.performance-circle .percentage {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 2px;
  color: var(--secondary-color, #6b7280);
}

.performance-circle .label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  opacity: 0.8;
  color: var(--secondary-color, #6b7280);
}

.attempt-meta .badge {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Questions Container */
.questions-container {
  margin-top: 1rem;
}

.question-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.question-card:hover {
  box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.question-header {
  background: #ffffff;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.question-number-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  background: var(--primary-color, #3b82f6);
  color: #ffffff;
  font-weight: 700;
  border-radius: 6px;
  margin-right: 1rem;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.question-text {
  font-weight: 600;
  color: #111827;
  font-size: 1rem;
  line-height: 1.5;
  padding-top: 4px;
}

.question-type-badge {
  margin-left: 1rem;
  flex-shrink: 0;
}

.type-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
}

.question-body {
  padding: 1.25rem;
}

/* Text Answer Section */
.text-answer-section {
  margin-top: 0.5rem;
}

.answer-box {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  overflow: hidden;
}

.answer-box-header {
  background: transparent;
  color: #0369a1;
  padding: 0.75rem 1rem 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.8125rem;
}

.answer-box-content {
  padding: 0 1rem 0.875rem 1rem;
  color: #1e293b;
  min-height: 3rem;
  line-height: 1.6;
}

.acceptable-answers-header {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #059669;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.acceptable-answers-list {
  background: #f0fdf4;
  border-left: 3px solid #10b981;
  border-radius: 4px;
  padding: 0.875rem 1rem;
}

.acceptable-answer-item {
  display: flex;
  align-items: flex-start;
  padding: 0.375rem 0;
  color: #065f46;
  line-height: 1.5;
}

.acceptable-answer-item:not(:last-child) {
  border-bottom: 1px solid #d1fae5;
  margin-bottom: 0.375rem;
  padding-bottom: 0.625rem;
}

.explanation-inline {
  color: #6b7280;
  font-style: italic;
}

/* Options Section */
.options-section {
  margin-top: 0.75rem;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  margin-bottom: 0.625rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  transition: all 0.2s ease;
}

.option-item:hover {
  background: #fafafa;
}

.option-correct {
  background: #ffffff;
  border-color: #10b981;
}

.option-selected-wrong {
  background: #ffffff;
  border-color: #ef4444;
}

.option-selected.option-correct {
  background: #ffffff;
  border-color: #10b981;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 0.875rem;
  font-size: 1rem;
  flex-shrink: 0;
}

.option-icon .fa-check-circle {
  color: #10b981;
}

.option-icon .fa-times-circle {
  color: #ef4444;
}

.option-icon .fa-circle {
  color: #d1d5db;
  font-size: 0.5rem;
}

.option-content {
  color: #111827;
  line-height: 1.5;
}

.option-text {
  font-weight: 500;
}

.option-badges {
  margin-left: auto;
  flex-shrink: 0;
}

.option-label {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  letter-spacing: 0.3px;
}

.label-correct {
  color: #10b981;
  background: #f0fdf4;
}

.label-wrong {
  color: #ef4444;
  background: #fef2f2;
}

/* Explanation Section */
.explanation-section {
  margin-top: 1.25rem;
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  overflow: hidden;
}

.explanation-header {
  background: transparent;
  color: #92400e;
  padding: 0.75rem 1rem 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
}

.explanation-header .fa-lightbulb {
  color: #f59e0b;
}

.explanation-content {
  padding: 0 1rem 0.875rem 1rem;
  color: #78350f;
  line-height: 1.6;
  font-size: 0.9375rem;
}

/* Tasks Container */
.tasks-container {
  margin-top: 1rem;
}

.task-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.task-card:hover {
  box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.task-header {
  background: #fafbfc;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.task-number-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  color: #ffffff;
  font-weight: 700;
  border-radius: 6px;
  margin-right: 1rem;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.task-text {
  font-weight: 600;
  color: #111827;
  font-size: 1rem;
  line-height: 1.5;
  padding-top: 4px;
}

.task-type-badge {
  margin-left: 1rem;
  flex-shrink: 0;
}

.type-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.task-body {
  padding: 1.25rem;
}

/* Upload Section */
.upload-section .file-attachment {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.upload-section .file-label {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8125rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.upload-section .download-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  color: #ffffff;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.8125rem;
  text-decoration: none;
  transition: all 0.15s ease;
}

.upload-section .download-btn:hover {
  opacity: 0.9;
  color: #ffffff;
  text-decoration: none;
}

.upload-section .submission-note {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  padding: 1rem;
}

.upload-section .note-label {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8125rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.upload-section .note-content {
  color: #1e293b;
  line-height: 1.6;
}

.upload-section .no-submission {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

/* Text Section */
.text-section .text-submission {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  padding: 1rem;
}

.text-section .submission-label {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8125rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.text-section .text-content {
  color: #1e293b;
  line-height: 1.7;
  max-height: 400px;
  overflow-y: auto;
}

.text-section .no-submission {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

/* Checkbox Section */
.interview-section {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  padding: 1rem;
}

.interview-label {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8125rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.interview-meta {
  color: #64748b;
  font-size: 0.8125rem;
}

.interview-summary {
  color: #1e293b;
  font-size: 0.875rem;
}

.transcript-content {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0.75rem;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.875rem;
  color: #1e293b;
  margin: 0;
}

.checkbox-section .checkbox-completed {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
  color: #1a1a1a;
  font-weight: 400;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.checkbox-section .checkbox-pending {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
  color: #9ca3af;
  font-weight: 400;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

/* Grading Section */
.grading-section {
  margin-top: 1.5rem;
}

.grading-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.grading-header {
  background: #f8fafc;
  padding: 1rem 1.25rem;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}

.grading-body {
  padding: 1.25rem;
}

.grading-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.grading-row:last-child {
  border-bottom: none;
}

.grading-label {
  font-weight: 500;
  color: #6b7280;
  font-size: 0.875rem;
}

.grading-value {
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
}

.grading-feedback {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.feedback-label {
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.feedback-content {
  color: #4b5563;
  line-height: 1.6;
  font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 768px) {
  .assignment-header-section {
    padding: 1rem;
  }

  .stats-cards {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .stat-card {
    padding: 1rem;
  }

  .stat-card .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 1.125rem;
    margin-right: 0.875rem;
  }

  .stat-card .stat-value {
    font-size: 1.25rem;
  }

  .total-attempts-badge {
    min-width: 90px;
    padding: 0.625rem 1rem;
  }

  .total-attempts-badge .attempts-number {
    font-size: 1.25rem;
  }

  .performance-circle {
    width: 70px;
    height: 70px;
  }

  .performance-circle .percentage {
    font-size: 1.125rem;
  }

  .question-number-badge {
    min-width: 28px;
    height: 28px;
    font-size: 0.8125rem;
  }

  .option-label {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
  }
}

@media (max-width: 576px) {
  .stats-cards {
    margin-top: 0.75rem;
  }

  .stat-card .stat-label {
    font-size: 0.7rem;
  }

  .stat-card .stat-value {
    font-size: 1.125rem;
  }
}
</style>


